import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { SectionLabel } from "@/components/ui/SectionLabel";
import { STATUS_TEXT } from "@/components/ui/statusTone";
import { usePod } from "@/features/pods/usePods";
import type { BioPod } from "@/features/pods/types";
import styles from "@/features/pods/PodDetailScreen.module.css";

/** Padding and stacking shared by every card on this screen. */
const CARD_BOX = "flex flex-col gap-3 p-5";

/**
 * Everything on this screen that no system produces yet.
 *
 * gaia-app had these spread through the markup, which makes it impossible to tell at a glance what
 * is real. Collected here so that wiring each one up is a visible, finite list - and so a reader
 * is never misled about which figures mean something.
 */
const PLACEHOLDER = {
  species: "Lavender",
  location: "Site Paris · ORION cluster A",
  bioRun: "BRC-2025-047",
  elapsed: "Day 31",
  growthStage: "Vegetative",
  growthPercent: 45,
  growthDay: "Day 14 of this stage",
  harvestDate: "June 12, 2025",
  harvestSub: "Approximately 26 days from now",
  harvestTag: "Flowers · Essential oil",
  updatedAt: "Updated a few seconds ago",
  events: [
    { label: "Nutrient solution check", time: "Today, 14:00" },
    { label: "pH adjustment + log", time: "Tomorrow, 10:00" },
    { label: "Weekly plant health inspection", time: "Thu, 09:00" },
  ],
} as const;

export function PodDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const { pod, isLoading, notFound, error } = usePod(id);

  if (isLoading) return <Notice>Loading pod…</Notice>;
  if (error !== null) return <Notice alert>{error}</Notice>;
  if (notFound || pod === null) return <Notice>No pod with the id “{id}”.</Notice>;

  return <PodDetail pod={pod} />;
}

/** A line of text where the screen would have been. */
function Notice({ children, alert = false }: { children: ReactNode; alert?: boolean }) {
  return (
    <PageContainer className="gap-6">
      <p role={alert ? "alert" : undefined} className="text-sm text-text-muted">
        {children}
      </p>
    </PageContainer>
  );
}

function PodDetail({ pod }: { pod: BioPod }) {
  return (
    <PageContainer className="gap-6">
      <div className={styles.titleRow}>
        <div>
          <h1 className="text-2xl font-semibold text-text">{pod.name}</h1>
          <span className="text-sm text-text-muted">Pod Status</span>
        </div>
        {/* A div, not a select: there is one pod per page and nothing to switch to yet. */}
        <div className="rounded-sm border border-border bg-surface px-3 py-2 text-sm text-text">
          {pod.id}
        </div>
      </div>

      <div className={styles.grid}>
        <aside className="flex flex-col gap-4">
          <Card className={CARD_BOX}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-lg font-semibold">{pod.id}</span>
              <Badge tone={pod.status} variant="outline">
                {pod.status.toUpperCase()}
              </Badge>
            </div>
            <div>
              <span className="text-base font-medium">{PLACEHOLDER.species}</span>
              <span className="text-xs text-text-muted">{PLACEHOLDER.location}</span>
            </div>
            <div className="mt-2 flex gap-6">
              <div>
                <SectionLabel block>Biorun</SectionLabel>
                <span className="text-sm font-medium text-text">{PLACEHOLDER.bioRun}</span>
              </div>
              <div>
                <SectionLabel block>Elapsed</SectionLabel>
                <span className="text-sm font-medium text-text">{PLACEHOLDER.elapsed}</span>
              </div>
            </div>
          </Card>

          <Card className={CARD_BOX}>
            <SectionLabel>Current growth stage</SectionLabel>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-accent" />
              <span className="text-xl font-semibold">{PLACEHOLDER.growthStage}</span>
            </div>
            <div
              className="h-[3px] overflow-hidden rounded-full bg-neutral-100"
              role="progressbar"
              aria-valuenow={PLACEHOLDER.growthPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Growth stage progress"
            >
              <div
                className="h-full rounded-full bg-accent"
                style={{ width: `${String(PLACEHOLDER.growthPercent)}%` }}
              />
            </div>
            <span className="text-xs text-text-muted">{PLACEHOLDER.growthDay}</span>
          </Card>

          <Card className={CARD_BOX}>
            <SectionLabel>Harvest forecast</SectionLabel>
            <div className="text-lg font-semibold">{PLACEHOLDER.harvestDate}</div>
            <div className="text-xs text-text-muted">{PLACEHOLDER.harvestSub}</div>
            <span className="inline-block rounded-full border border-podpink-300 px-3 py-0.5 text-xs text-podpink-600">
              {PLACEHOLDER.harvestTag}
            </span>
          </Card>
        </aside>

        <div className="flex flex-col gap-4">
          <Card className={CARD_BOX}>
            <div className="flex items-center justify-between gap-3">
              <SectionLabel>Live sensor readings</SectionLabel>
              <span className="text-xs text-text-muted">{PLACEHOLDER.updatedAt}</span>
            </div>
            <div className={styles.sensorGrid}>
              {pod.sensors.map((sensor) => (
                <div
                  key={sensor.label}
                  className="flex flex-col gap-2 rounded-lg border border-border p-4"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold tracking-[0.05em] text-text-muted">
                      {sensor.label.toUpperCase()}
                    </span>
                    <span className={`text-xs font-semibold ${STATUS_TEXT[sensor.status]}`}>
                      {sensor.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="font-mono text-2xl font-semibold text-text">
                    {sensor.value}
                    <span className="ml-0.5 text-base text-text-muted">{sensor.unit}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={STATUS_TEXT[sensor.status]} aria-hidden>
                      {sensor.status === "nominal" ? "✓" : "!"}
                    </span>
                    <span className="text-xs text-text-muted">
                      {sensor.status === "nominal" ? "Within expected range" : "Outside range"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className={styles.twoCol}>
            <Card className={CARD_BOX}>
              <div className="flex items-center justify-between gap-3">
                <SectionLabel>Active alerts</SectionLabel>
                <Badge tone="nominal" variant="outline">
                  0 OPEN
                </Badge>
              </div>
              <div className="flex flex-col items-center gap-1 py-6 text-center">
                <p className="text-base font-medium text-text">All systems nominal</p>
                <p className="text-sm text-text-muted">
                  No open anomalies or equipment issues on this asset
                </p>
              </div>
            </Card>

            <Card className={CARD_BOX}>
              <SectionLabel>Upcoming events</SectionLabel>
              <div className="flex flex-col gap-3">
                {PLACEHOLDER.events.map((event) => (
                  <div key={event.label} className="flex items-center gap-3">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span className="flex-1 text-sm text-text">{event.label}</span>
                    <span className="text-xs text-text-muted">{event.time}</span>
                  </div>
                ))}
                <Link to="/calendar" className="mt-2 self-end text-sm text-accent">
                  View full event calendar →
                </Link>
              </div>
            </Card>
          </div>

          <Card className="flex flex-row flex-wrap items-center justify-between gap-4 p-5">
            <div>
              <SectionLabel block>Export report</SectionLabel>
              <div className="mt-1 text-xs text-text-muted">
                Current BioRun · SOP logs, yield trajectory, parameter history
              </div>
            </div>
            <div className="flex shrink-0 gap-3">
              {/* Disabled rather than silently inert: nothing generates these reports yet. */}
              <Button disabled title="Report export is not implemented yet">
                Export as XLSX
              </Button>
              <Button variant="primary" disabled title="Report export is not implemented yet">
                Export as PDF
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
}
